import React from 'react';
import cx from 'classnames';

import { ModalProvider, useModalContext } from '@magoosh/context/modal_context';
import { formatPrice } from 'utilities/money';

import { Col, Collapse, Grid, Row } from 'react-bootstrap';
import { DefaultButton, SecondaryButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import Confirmation from './confirmation';

import { AddOnOffer } from './types';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';
import { paths } from 'config/path_helpers';

const CLASS_DETAILS = [
  {
    description: (
      <>
        <p>
          The #1 reason otherwise competitive candidates get rejected is due to a weak career vision. You
          don’t want your “why this degree?” rationale to be weak, vague, or bore the admissions committee
          into a coma.
        </p>
        <p>
          The <b>Career Vision</b> and <b>Why MBA?</b> modules work hand-in-hand to help you create a strong
          rationale that you will use across every application.
        </p>
      </>
    ),
    name: 'Strategy'
  },
  {
    description: (
      <>
        <p>
          Yes, the resume is actually more important than the essays (!) and no, you can’t simply recycle the
          same resume you used to get your last job.
        </p>
        <p>
          The <b>Resume</b> module walks you step-by-step through key “do’s” and “don’ts”.
        </p>
      </>
    ),
    name: 'A Winning Resume'
  },
  {
    description: (
      <>
        <p>
          Stories are the core elements of any successful application. Admissions committees are looking for
          candidates who possess certain brand (personality) traits, and stories are the “evidence” you’re
          going to provide as proof that you’ve got the goods.
        </p>
        <p>
          The <b>Brainstorming</b> and <b>Branding</b> modules will help you dig deep to uncover your most
          powerful stories and giving you a framework to analyze/judge them so that at a later step, you can
          best match each story to each school you’re applying to.
        </p>
      </>
    ),
    name: 'Develop Your Stories'
  },
  {
    description: (
      <>
        <p>
          Fun fact: different schools are looking for different things. A story that might enthrall one
          school’s admissions committee might completely miss the mark for another.
        </p>
        <p>
          The <b>Mapping</b> module teaches you how to present your stories to the right schools.
        </p>
        <p>It will teach you:</p>
        <ul>
          <li>Brand traits different schools look for</li>
          <li>How to approach each essay – even the questions that make you go: “Huh?”</li>
          <li>What to mention – and what NOT to mention</li>
        </ul>
      </>
    ),
    name: 'School-Specific Essay Guidance'
  },
  {
    description: (
      <>
        <p>
          The <b>Pre-writing</b> module breaks down each essay for each school into easily manageable
          “mini-questions.” By giving you a more “bite-sized” way to start, procrastination fades away – plus,
          you’ll be sure you’re answering the question appropriately. As an added bonus, you can use this
          module as a final check-list before hitting “submit”!
        </p>
        <p>
          The <b>Rough Drafts</b> and <b>Final Drafts</b> modules give you tools and tips to finalize your
          essays while still keeping them in your authentic voice.
        </p>
      </>
    ),
    name: 'Writing Made Easy'
  },
  {
    description: (
      <p>
        Use our specially designed tool to align your strategy in our <b>Recommendations</b> module. We’ll
        walk you step-by-step through your best stories, data points, and on-the-job experiences so that your
        recommendations come back as both authentic and descriptive.
      </p>
    ),
    name: 'Get the Best Recommendations'
  },
  {
    description: (
      <>
        <p>You know you need to prepare for the interview, but you’re just not sure where to start?</p>
        <p>
          ApplicantLab’s <b>Interview</b> module will help you ace that high-pressure moment! We’ll make sure
          you know what to expect and that you’re ready to put your best foot forward.
        </p>
      </>
    ),
    name: 'Ace Your Interview'
  },
  {
    description: (
      <p>
        ApplicantLab has a ton of individual features and modules to help you assemble your MBA Application –
        from critical to-do lists, to info on how to fill out the application itself, there are tons of tips
        and tricks to be sure you don’t miss a beat.
      </p>
    ),

    name: 'And So Much More!'
  }
];

const APPLICANT_LAB_UNIVERSITY_ICONS = [
  {
    src: 'https://www.applicantlab.com/wp-content/uploads/2021/03/stanford-logo_bw-1.png',
    alt: 'Stanford Graduate School of Business'
  },
  {
    src: 'https://www.applicantlab.com/wp-content/uploads/2021/03/wharton-logo_bw-1.png',
    alt: 'Wharton University of Pennsylvania'
  },
  {
    src: 'https://www.applicantlab.com/wp-content/uploads/2021/03/hbs-logo_bw-1.png',
    alt: 'Harvard Business School'
  },
  {
    src: 'https://www.applicantlab.com/wp-content/uploads/2021/03/chicago-booth-logo_bw-1.png',
    alt: 'The University of Chicago Booth School of Business'
  },
  {
    src: 'https://www.applicantlab.com/wp-content/uploads/2021/03/kellogg-logo_bw-1.png',
    alt: 'Northwestern Kellogg School of Management'
  },
  {
    src: 'https://www.applicantlab.com/wp-content/uploads/2021/03/mit-logo_bw-1.png',
    alt: 'MIT Sloan School of Management'
  }
];

const ApplicantLabAddOn: React.FC<AddOnOffer> = (props) => {
  const { discountedPrice, fullPrice } = props.price;

  const { setModal } = useModalContext();

  const handleAddOnPurchaseClicked = () => {
    if (props.paymentDetails) {
      setModal({
        header: 'Add ApplicantLab',
        body: (
          <Confirmation
            addOnName="ApplicantLab"
            handleClose={() => setModal(null)}
            successMessage="You'll receive an email with information about how you can activate your ApplicantLab account when you're ready to continue your admissions journey."
            {...props}
          />
        )
      });
    } else {
      window.location.href = `${paths.subscriptions(props.orderItem.purchasable.planSlug)}?coupon=${props.price.couponCode}`;
    }
  };

  const fiveStars: React.FC = () => {
    return (
      <span style={{ color: colors.statusYellow }}>
        <FontAwesomeIcon icon="star" />
        <FontAwesomeIcon icon="star" />
        <FontAwesomeIcon icon="star" />
        <FontAwesomeIcon icon="star" />
        <FontAwesomeIcon icon="star" />
      </span>
    );
  };

  return (
    <Grid className="u-padding-T-m u-padding-B-xl">
      <Row>
        <Col md={6} mdOffset={3}>
          <h4 className="text-gray">One-Time Offer</h4>
          <h2 className="u-margin-T-n">
            Add ApplicantLab for{' '}
            {discountedPrice !== fullPrice && (
              <>
                <span style={{ textDecoration: 'line-through' }}>{formatPrice(fullPrice, 'usd', true)}</span>{' '}
              </>
            )}
            {formatPrice(discountedPrice, 'usd', true)}!
          </h2>
        </Col>
      </Row>

      <Row>
        <Col md={6} mdOffset={3}>
          <hr className="u-margin-T-n" />

          <p>
            A strong GMAT score is just one part of your MBA admissions journey! You’ll also need compelling
            essays, a well-structured resume, high-quality recommendations, and more.{' '}
            <strong>ApplicantLab</strong> uses interactive exercises and extensive, tailored videos to provide
            the same admissions advice as pricey 1-on-1 consultants, all on a convenient 24/7 online platform.
          </p>
          <p>
            {discountedPrice !== fullPrice ? (
              <>
                <strong>
                  Sign up now to get a {formatPrice(fullPrice - discountedPrice, 'usd', true)} discount
                </strong>{' '}
                (regular price is {formatPrice(fullPrice, 'usd', true)}, and you can activate your one-year
                subscription when you’re ready! But don't wait too long — the sooner you know what admissions
                officers are looking for, the sooner you can start making your profile as competitive as
                possible.
              </>
            ) : (
              <>Sign up now and you can activate your one-year subscription when you’re ready!</>
            )}
          </p>

          <div className={cx('d-flex u-margin-V-l', style.ctas)}>
            <SecondaryButton onClick={handleAddOnPurchaseClicked}>
              Yes, Add <span className="hidden-xs">ApplicantLab</span> for{' '}
              {formatPrice(discountedPrice, 'usd', true)}
            </SecondaryButton>

            {props.nextPath && <DefaultButton className="u-margin-L-s" href={props.nextPath}>
              No Thanks
            </DefaultButton>}
          </div>

          <div
            className="border-radius-all u-margin-V-l u-padding-A-m"
            style={{ backgroundColor: '#F5EEFC' }}>
            <p className="text-uppercase">
              <strong>14 Comprehensive Modules</strong>
            </p>

            {CLASS_DETAILS.map((classDetail, idx) => (
              <ClassDetail key={idx} classNumber={idx + 1} {...classDetail} />
            ))}
          </div>

          <div className={'u-margin-T-xl'}>
            <div className="u-margin-B-s">
              <h5>
                <strong>
                  <u>Here’s where ApplicantLab users are routinely accepted</u>
                </strong>
              </h5>
              <div className={style.applicantLabSchoolLogos}>
                {APPLICANT_LAB_UNIVERSITY_ICONS.map((icon) => (
                  <img src={icon.src} alt={icon.alt} title={icon.alt} />
                ))}
              </div>
            </div>
          </div>

          <div className={cx(style.reviewBox, 'd-flex u-margin-T-xl')}>
            <img
              alt="ApplicantLab logo"
              src="https://www.applicantlab.com/wp-content/uploads/2021/03/AL_logo.png"
            />

            <div className="u-margin-L-s">
              {fiveStars(null)}
              <p>“Wonderful Experience - Highly Recommend”</p>
              {fiveStars(null)}
              <p>“Don’t miss out on Applicant Lab, it got me into Yale”</p>
              {fiveStars(null)}
              <p>“Great support, great value and great advice”</p>
              <p>
                <a
                  href="https://gmatclub.com/reviews/applicantlab/applicantlab-on-demand-admissions-guidance-tool-345345819"
                  target="_blank">
                  Read more verified reviews on GMATClub.com
                </a>
              </p>
            </div>
          </div>

          <div className="u-margin-T-xl">
            <h5>
              <strong>
                <u>Meet ApplicantLab's Founder</u>
              </strong>
            </h5>

            <div>
              <div className={style.founderHeader}>
                <img src="https://www.applicantlab.com/wp-content/uploads/2021/03/Maria.png" />
                <p>
                  Maria Wich-Vila
                  <br />
                  <i>MBA, Harvard Business School, 2005</i>
                  <br />
                  <i>BSE, Princeton, 1998</i>
                </p>
              </div>

              <p>
                Maria has over 15 years of MBA admissions experience beginning with volunteering with the HBS
                Admissions office in 2003. With the belief that expert admissions advice shouldn’t cost
                thousands, and should be accessible to people from many income levels, she founded
                ApplicantLab in an effort to level the MBA admissions playing field.
              </p>
              <p>
                Maria currently co-hosts the Poets & Quants weekly podcast “Business Casual” and is regularly
                invited to present on GMATClub.com webinars.{' '}
                <a href="https://poetsandquants.com/consultant/maria-wich-vila/" target="_blank">
                  Read what ApplicantLab users say about Maria on Poets & Quants
                </a>
                .
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </Grid>
  );
};

interface ClassDetailProps {
  classNumber: number;
  description: React.ReactNode;
  name: string;
}

const ClassDetail: React.FC<ClassDetailProps> = ({ classNumber, description, name }) => {
  const [isExpanded, setIsExpanded] = React.useState(classNumber === 1);

  return (
    <div className="u-margin-T-s">
      <div onClick={() => setIsExpanded(!isExpanded)} style={{ cursor: 'pointer' }}>
        <strong>
          <span className="u-margin-R-xs" style={{ color: colors.brandFuchsia }}>
            <span className="u-margin-R-xxs">
              <FontAwesomeIcon icon={isExpanded ? 'minus' : 'plus'} />
            </span>
            <span>{name}</span>
          </span>
        </strong>
      </div>

      <Collapse in={isExpanded}>
        <div>{description}</div>
      </Collapse>
    </div>
  );
};

const ApplicantLabAddOnWithProvider: React.FC<AddOnOffer> = (props) => {
  return (
    <ModalProvider>
      <ApplicantLabAddOn {...props} />
    </ModalProvider>
  );
};

export default ApplicantLabAddOnWithProvider;
